import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RefreshCw, Plus, MoreVertical } from "lucide-react";
import Select from "react-select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import notificationService from "../services/notificationService";
import CreateNotificationDrawer from "../components/notifications/CreateNotificationDrawer";
import { CATEGORY_META, STATUS_META, pillStyle, formatUtcToLocal } from "../helpers/notificationMeta";

const CATEGORY_FILTERS = [
  { value: "", label: "All Categories" },
  { value: "release_note", label: "Release Notes" },
  { value: "model_deprecation", label: "Model Deprecation" },
  { value: "emergency", label: "Emergency" },
];

const STATUS_FILTERS = [
  { value: "", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

// Matches the filter dropdowns on Support Tickets (components/FilterMultiSelect.jsx).
const STATUS_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    borderColor: state.isFocused ? "rgba(139,92,246,0.5)" : "#ECEEF4",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(139,92,246,0.1)" : "none",
    "&:hover": { borderColor: state.isFocused ? "rgba(139,92,246,0.5)" : "#D9DCE6" },
    height: "44px",
    paddingLeft: "8px",
    fontSize: "0.875rem",
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "#9AA1B2", paddingRight: "12px" }),
  singleValue: (base) => ({ ...base, color: "#4B5266", fontWeight: 500 }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    border: "1px solid #ECEEF4",
    boxShadow: "0 8px 24px rgba(20,18,43,0.08)",
    overflow: "hidden",
    fontSize: "0.875rem",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused || state.isSelected ? "#F5F2FF" : "white",
    color: "#14122B",
    fontWeight: state.isSelected ? 600 : 400,
    cursor: "pointer",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
};

const Notifications = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Pagination state — mirrors Campaigns/SupportTickets' offset pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Guards against the classic out-of-order-response race: a filter click
  // fired while the previous (e.g. initial unfiltered) request is still
  // in flight starts a second request, and network timing gives no
  // guarantee the two settle in the order they were sent — without this,
  // whichever happens to resolve *last* wins and can overwrite the chip's
  // already-correct selection with stale, unfiltered data.
  const latestRequestIdRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current;
    try {
      const data = await notificationService.getNotificationsList({
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        page,
        pageSize: limit,
      });
      if (requestId !== latestRequestIdRef.current) return; // superseded by a newer request
      const notifications = data?.items || [];
      const totalCount = data?.total ?? notifications.length;
      setItems(notifications);
      setTotal(totalCount);
      setTotalPages(Math.max(1, Math.ceil(totalCount / limit)));
      setError(null);
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) return;
      console.error("Failed to fetch notifications:", err);
      setError("Failed to load notifications.");
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [categoryFilter, statusFilter, page, limit]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchNotifications();
  };

  const handleDrawerClose = (saved) => {
    setDrawerOpen(false);
    setEditingItem(null);
    if (saved) {
      setPage(1);
      fetchNotifications();
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setDrawerOpen(true);
  };

  const handlePublish = async (id) => {
    try {
      await notificationService.publishNotification(id);
      toast.success("Notification published");
      fetchNotifications();
    } catch (err) {
      toast.error("Failed to publish notification");
    }
  };

  const handleArchive = async (id) => {
    try {
      await notificationService.archiveNotification(id);
      toast.success("Notification archived");
      fetchNotifications();
    } catch (err) {
      toast.error("Failed to archive notification");
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationService.deleteNotification(id);
      toast.success("Draft deleted");
      fetchNotifications();
    } catch (err) {
      toast.error("Failed to delete notification");
    }
  };

  const audienceLabel = (item) =>
    item.audience_type === "ALL"
      ? "All facilities"
      : `${(item.target_facility_ids || []).length} facilit${
          (item.target_facility_ids || []).length === 1 ? "y" : "ies"
        }`;

  const dateLabel = (item) =>
    formatUtcToLocal(item.published_at || item.scheduled_for || item.created_at);

  return (
    <div className="w-full flex flex-col h-full lg:h-[calc(100vh-100px)] pt-4 gap-3">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
        {/* Phones: the category pills sit in one row you can swipe sideways */}
        <div className="table-scroll flex w-full gap-2 overflow-x-auto pb-1 sm:contents">
        {CATEGORY_FILTERS.map(({ value, label }) => (
          <button
            key={value || "all-categories"}
            onClick={() => {
              setPage(1);
              setCategoryFilter(value);
            }}
            className={`h-11 flex-shrink-0 px-4 rounded-full sm:rounded-xl text-sm font-medium whitespace-nowrap border transition-colors ${
              categoryFilter === value
                ? "bg-[#14122B] border-[#14122B] text-white font-semibold"
                : "bg-white border-[#ECEEF4] text-[#4B5266] hover:border-[#D9DCE6]"
            }`}
          >
            {value === "" ? (
              <>
                <span className="sm:hidden">All</span>
                <span className="hidden sm:inline">{label}</span>
              </>
            ) : (
              label
            )}
          </button>
        ))}
        </div>

        {/* Phones: status dropdown, refresh and New share one row */}
        <div className="flex w-full items-center gap-2 sm:contents">
        {/* Same look as the "All Status" filter on Support Tickets */}
        <div className="min-w-0 flex-1 sm:min-w-[11.5rem] sm:flex-none">
          <Select
            isSearchable={false}
            options={STATUS_FILTERS}
            value={STATUS_FILTERS.find((option) => option.value === statusFilter) || STATUS_FILTERS[0]}
            onChange={(selected) => {
              setPage(1);
              setStatusFilter(selected.value);
            }}
            classNamePrefix="react-select"
            styles={STATUS_SELECT_STYLES}
            menuPortalTarget={document.body}
          />
        </div>

        {/* Phones: one row with the refresh icon + New Notification; from sm up: full labels */}
        <div className="flex flex-shrink-0 flex-nowrap items-center justify-end gap-2 sm:ml-auto sm:gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh"
            aria-label="Refresh"
            className="flex flex-shrink-0 items-center justify-center gap-2 w-11 sm:w-auto sm:px-4 h-11 text-sm font-medium whitespace-nowrap text-[#4B5266] rounded-xl border border-[#ECEEF4] bg-white hover:border-[#D9DCE6] hover:bg-gray-50 transition disabled:opacity-50"
          >
            <RefreshCw size={14} className={isRefreshing ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              setDrawerOpen(true);
            }}
            className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2 px-4 sm:px-6 h-11 text-sm font-semibold whitespace-nowrap rounded-xl bg-[#3F4BB8] text-white hover:bg-[#363FA0] transition"
          >
            <Plus size={16} />
            New<span className="hidden sm:inline"> Notification</span>
          </button>
        </div>
        </div>
      </div>

      {/* Phones: result count above the list, like the mobile design */}
      <p className="text-sm text-[#4B5266] sm:hidden">
        {total} notification{total === 1 ? "" : "s"}
      </p>

      {/* Table card (same look as Feedback Items / Support Tickets). It grows with
          content; the page scrolls, not a nested region, so overflow is never
          hidden behind an easy-to-miss inner scrollbar (see notification QA BUG-11). */}
      <div className="flex-1 min-h-0 bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
        <div className="table-scroll h-full overflow-auto">
        <table className="w-full min-w-[1100px] xl:min-w-[800px] table-fixed border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {[
                { label: "Title", width: "28%" },
                { label: "Category", width: "18%" },
                { label: "Audience", width: "16%" },
                { label: "Status", width: "14%" },
                { label: "Date", width: "16%" },
                { label: "", width: "8%" },
              ].map(({ label, width }) => (
                <th
                  key={label || "actions"}
                  style={{ position: "sticky", top: 0, zIndex: 20, background: "#F5F2FF", width, textAlign: "left" }}
                  className="py-3.5 pl-6 pr-3 align-middle text-xs font-semibold text-gray-500 uppercase tracking-wide"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2B3674] mr-3" />
                    Loading notifications...
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-sm text-red-600">
                  {error}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                  No notifications found
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const categoryMeta = CATEGORY_META[item.category] || {};
                const statusMeta = STATUS_META[item.status] || {};
                const CategoryIcon = categoryMeta.icon;

                return (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/notifications/${item.id}`)}
                    className="cursor-pointer border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50"
                  >
                    <td className="pl-6 pr-3 py-4 align-middle text-sm font-semibold text-[#14122B]">
                      <div className="line-clamp-1" title={item.title}>
                        {item.title}
                      </div>
                    </td>

                    <td className="pl-6 pr-3 py-4 align-middle">
                      <span style={pillStyle(categoryMeta.color || "#6b7280")}>
                        {CategoryIcon && <CategoryIcon size={12} />}
                        {categoryMeta.label || item.category}
                      </span>
                    </td>

                    <td className="pl-6 pr-3 py-4 align-middle text-gray-600">{audienceLabel(item)}</td>

                    <td className="pl-6 pr-3 py-4 align-middle">
                      <span style={pillStyle(statusMeta.color || "#6b7280")}>
                        {statusMeta.label || item.status}
                      </span>
                    </td>

                    <td className="pl-6 pr-3 py-4 align-middle text-gray-600">{dateLabel(item)}</td>

                    <td className="pl-6 pr-3 py-4 align-middle" onClick={(e) => e.stopPropagation()}>
                      {(item.status === "draft" ||
                        item.status === "scheduled" ||
                        item.status === "published") && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1.5 rounded-md hover:bg-gray-200 text-gray-400">
                              <MoreVertical size={18} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                            {(item.status === "draft" || item.status === "scheduled") && (
                              <DropdownMenuItem onClick={() => handleEdit(item)}>
                                Edit
                              </DropdownMenuItem>
                            )}
                            {(item.status === "draft" || item.status === "scheduled") && (
                              <DropdownMenuItem onClick={() => handlePublish(item.id)}>
                                Publish now
                              </DropdownMenuItem>
                            )}
                            {item.status === "published" && (
                              <DropdownMenuItem onClick={() => handleArchive(item.id)}>
                                Archive
                              </DropdownMenuItem>
                            )}
                            {item.status === "draft" && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(item.id)}
                                className="text-red-600"
                              >
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination (same layout as Feedback Items / User Management) */}
      <div className="mt-auto pt-1 pb-1 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm flex-shrink-0">
        <span>Total {total}</span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            Rows per page
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="rounded-md border px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-50">
              &lsaquo;
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-50"
            >
              &rsaquo;
            </button>
          </div>
        </div>
      </div>

      <CreateNotificationDrawer open={drawerOpen} onClose={handleDrawerClose} editTarget={editingItem} />
    </div>
  );
};

export default Notifications;
